import { useCallback, useEffect, useRef, type ReactNode } from 'react';
import { useNavigate, useSearchParams } from 'react-router';

/**
 * The route-driven drawer that Cluster access opens its detail surfaces in.
 *
 * Six call sites (project, mappings, fleet, commands, one command, one address), so
 * the accessibility contract — focus trap, Esc, focus return, scroll lock, labelled
 * dialog — is written once here rather than six times in six pages.
 *
 * WHY A DRAWER AND NOT A ROUTE SWAP. The landing screen stays mounted underneath.
 * That is the whole point of the merge: the capacity band and the row that sent you
 * here are still on screen while you read the detail, so "these are one mechanism"
 * is something you can see rather than something the docs claim.
 *
 * SEARCH-PARAM NAMESPACES. The drawer and the screen behind it share ONE address
 * bar. Every page hosted in a drawer already owns unprefixed keys (`tab`, `state`,
 * `cluster`, `q`, `sort`, `page`, `liveq`, `livepage`), and those pages are mounted
 * unchanged — so the landing screen's own table state is namespaced `ip*` instead.
 * Without that, filtering the row table would silently retab the project drawer:
 * both wanted `state`.
 *
 * Closing therefore keeps the `ip*` keys and drops everything else, which is what
 * puts you back on the filtered table you left rather than at the top of an
 * unfiltered one.
 */
export function Drawer({
  title,
  children,
  onClose,
}: {
  title: string;
  children: ReactNode;
  /**
   * Local-state drawers (the peer timeline) pass their own close; the route-driven
   * ones on Cluster access leave it unset and keep the `/cluster-access` return.
   */
  onClose?: () => void;
}) {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const panelRef = useRef<HTMLDivElement>(null);
  // Whatever had focus when the drawer opened — a row link on the landing table.
  // Captured on mount, restored on unmount, so keyboard users are not dropped at
  // the top of the document every time they read a detail and come back.
  const openerRef = useRef<Element | null>(null);

  const close = useCallback(() => {
    if (onClose) {
      onClose();
      return;
    }
    const keep = new URLSearchParams();
    for (const [k, v] of params.entries()) if (k.startsWith('ip')) keep.set(k, v);
    const search = keep.toString();
    navigate({ pathname: '/cluster-access', search: search ? `?${search}` : '' });
  }, [navigate, params, onClose]);

  useEffect(() => {
    openerRef.current = document.activeElement;
    const { overflow } = document.body.style;
    document.body.style.overflow = 'hidden';
    // Move focus into the panel so the first Tab lands inside the dialog rather
    // than continuing through the page behind it.
    panelRef.current?.focus();
    return () => {
      document.body.style.overflow = overflow;
      const opener = openerRef.current;
      if (opener instanceof HTMLElement && document.contains(opener)) opener.focus();
    };
  }, []);

  const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Escape') {
      e.stopPropagation();
      close();
      return;
    }
    if (e.key !== 'Tab') return;
    const panel = panelRef.current;
    if (!panel) return;
    const focusable = panel.querySelectorAll<HTMLElement>(
      'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
    );
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    const active = document.activeElement;
    if (e.shiftKey && (active === first || active === panel)) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && active === last) {
      e.preventDefault();
      first.focus();
    }
  };

  return (
    <div
      className="ca-drawer__scrim"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) close();
      }}
    >
      <div
        ref={panelRef}
        className="ca-drawer"
        role="dialog"
        aria-modal="true"
        aria-label={title}
        tabIndex={-1}
        onKeyDown={onKeyDown}
      >
        {/* No heading of its own: the page mounted inside supplies the visible
            <h2>, and a second one here would give the dialog two titles. `title`
            is the accessible name only. */}
        <div className="ca-drawer__head">
          <button type="button" className="btn btn-ghost" onClick={close}>
            Close
          </button>
        </div>
        <div className="ca-drawer__body">{children}</div>
      </div>
    </div>
  );
}
